import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { RoleSelectScreen } from './RoleSelectScreen';
import { JoinCodeScreen } from './JoinCodeScreen';
import { useAuth } from '@/hooks/useAuth';
import { useHuntStore } from '@/stores/huntStore';
import { huntService, HuntPreviewResponse } from '@/services/api/huntService';
import { ParticipantRole } from '@gps-hunt/shared';
import { Button } from '@/components/common/Button';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { Users, Play, X } from 'lucide-react';

type JoinStep = 'code' | 'preview' | 'role';

/**
 * Hunt Preview Screen - Shows hunt details before joining
 */
interface HuntPreviewScreenProps {
  preview: HuntPreviewResponse;
  joinCode: string;
  onConfirm: () => void;
  onCancel: () => void;
  isLoading?: boolean;
}

const HuntPreviewScreen: React.FC<HuntPreviewScreenProps> = ({
  preview,
  joinCode,
  onConfirm,
  onCancel,
  isLoading = false,
}) => {
  return (
    <div className="h-full flex flex-col items-center justify-center p-6 overflow-hidden">
      <div className="w-full max-w-md bg-surface rounded-2xl p-6 space-y-6">
        {/* Header */}
        <div className="text-center">
          <div className="text-xs font-mono text-primary mb-2">JOIN CODE: {joinCode}</div>
          <h1 className="text-2xl font-bold text-text-primary">{preview.name}</h1>
          {preview.description && (
            <p className="text-text-secondary mt-2">{preview.description}</p>
          )}
        </div>

        {/* Creator and participant info */}
        <div className="flex items-center justify-center gap-6 py-4 border-y border-surface-light">
          <div className="text-center">
            <div className="text-xs text-text-muted uppercase">Created by</div>
            <div className="font-medium text-text-primary">{preview.creatorDisplayName}</div>
          </div>
          <div className="w-px h-8 bg-surface-light" />
          <div className="text-center">
            <div className="text-xs text-text-muted uppercase">Participants</div>
            <div className="flex items-center justify-center gap-1 font-medium text-text-primary">
              <Users className="w-4 h-4" />
              <span>{preview.participantCount}</span>
            </div>
          </div>
        </div>

        {/* Action buttons */}
        <div className="flex gap-3">
          <Button
            variant="secondary"
            onClick={onCancel}
            disabled={isLoading}
            className="flex-1 flex items-center justify-center gap-2"
          >
            <X className="w-4 h-4" />
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={onConfirm}
            disabled={isLoading}
            className="flex-1 flex items-center justify-center gap-2"
          >
            {isLoading ? (
              <>
                <LoadingSpinner />
                Loading...
              </>
            ) : (
              <>
                <Play className="w-4 h-4" />
                Join Hunt
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

/**
 * Join Hunt Flow - Multi-step wizard for joining a hunt
 * Note: User is already authenticated (protected route)
 */
export const JoinHuntFlow: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const { setCurrentHunt, setMyRole } = useHuntStore();

  const [step, setStep] = useState<JoinStep>('code');
  const [huntPreview, setHuntPreview] = useState<HuntPreviewResponse | null>(null);
  
  // Use refs to store values that need to persist across renders
  const joinCodeRef = useRef('');
  const displayNameRef = useRef(user?.displayName || '');
  
  // Update displayName ref when user loads
  useEffect(() => {
    if (user?.displayName && !displayNameRef.current) {
      displayNameRef.current = user.displayName;
    }
  }, [user]);
  
  const [isLoading, setIsLoading] = useState(false);
  const [error, setJoinError] = useState<string | null>(null);
  // Key to force JoinCodeScreen remount and clear form
  const [codeScreenKey, setCodeScreenKey] = useState(0);

  const handleCodeSubmit = async (code: string, name: string) => {
    joinCodeRef.current = code;
    displayNameRef.current = name;
    setJoinError(null);
    setIsLoading(true);

    try {
      // Get hunt preview
      const preview = await huntService.getHuntPreview(code);
      setHuntPreview(preview);
      // Clear the code screen for next use
      setCodeScreenKey(k => k + 1);
      setStep('preview');
    } catch {
      // Always show a friendly error message for invalid join codes
      setJoinError('Invalid join code. Please check the code and try again.');
      // Clear the code boxes so user can enter a new code
      setCodeScreenKey(k => k + 1);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePreviewConfirm = () => {
    setStep('role');
  };

  const handlePreviewCancel = () => {
    setHuntPreview(null);
    setJoinError(null);
    // Clear the code screen when canceling
    setCodeScreenKey(k => k + 1);
    setStep('code');
  };

  const handleRoleSelect = async (role: ParticipantRole) => {
    const joinCode = joinCodeRef.current;
    const displayName = displayNameRef.current;
    console.log('[JoinHuntFlow] handleRoleSelect:', { joinCode, displayName, role });
    setIsLoading(true);
    setJoinError(null);

    try {
      const result = await huntService.joinHunt(joinCode, displayName, role);
      
      // Update store
      setCurrentHunt({
        id: result.huntId,
        joinCode: result.joinCode,
        status: result.status,
        pinOrder: [],
        hidersReady: [],
        completedPinIds: [],
        createdAt: new Date().toISOString(),
        createdBy: '',
        name: huntPreview?.name || 'Hunt',
        creatorDisplayName: huntPreview?.creatorDisplayName || 'Unknown',
      });
      setMyRole(role);

      // Navigate to lobby
      navigate(`/lobby/${result.huntId}`);
    } catch (err) {
      const rawMessage = err instanceof Error ? err.message : '';
      
      // Show user-friendly messages
      if (rawMessage.includes('Invalid') || rawMessage.includes('not found') || rawMessage.includes('join code')) {
        setJoinError('Invalid join code. Please check the code and try again.');
        setCodeScreenKey(k => k + 1);
        setStep('code');
      } else {
        setJoinError(rawMessage || 'Failed to join hunt. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  switch (step) {
    case 'code':
      return (
        <JoinCodeScreen 
          key={codeScreenKey}
          onSubmit={handleCodeSubmit}
          isLoading={isLoading}
          error={error}
          initialDisplayName={displayNameRef.current}
        />
      );
    
    case 'preview':
      if (!huntPreview) {
        setStep('code');
        return null;
      }
      return (
        <HuntPreviewScreen
          preview={huntPreview}
          joinCode={joinCodeRef.current}
          onConfirm={handlePreviewConfirm}
          onCancel={handlePreviewCancel}
          isLoading={isLoading}
        />
      );
    
    case 'role':
      return (
        <RoleSelectScreen 
          onSelect={handleRoleSelect}
          isLoading={isLoading}
        />
      );
    
    default:
      return null;
  }
};

export default JoinHuntFlow;
